import React, { useEffect, useState, useRef, createContext } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars, faExpand, faCompress,
  faUser, faSignOutAlt, faSearch,
  faTachometerAlt, faCalendarCheck, faHistory,
  faSync, faChevronRight, faChevronLeft, faHome,
  faCalendarDays
} from '@fortawesome/free-solid-svg-icons';

import Sidebar from './Sidebar';
import Logo from '../../../assetes/logos.png';
import ManagePresences from '../erp/ManagePresences';
import PresenceDashboard from '../erp/PresenceDashboard';
import HistoriquePresence from '../erp/HistoriquePresence';
import SidebarRH from './SidebarRH';
import PresenceGrouped from '../erp/PresenceGrouped';
import SidebarCOMPTABLE from './SidebarCOMPTABLE';

// ============================================================
// CONTEXTE PARTAGÉ POUR LES PRÉSENCES (refresh, source)
// ============================================================
export const PresencesContext = createContext();

// ===================================================================
// COMPOSANT PRINCIPAL
// ===================================================================
const CustomPresences = ({ userId, fonction }) => {
  const [theme, setTheme] = useState('dark');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [userData] = useState({ name: 'Administrateur', role: 'SUPER ADMIN' });
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const dropdownRef = useRef(null);

  // Sidebar collapsible
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Recherche mobile
  const [searchQuery, setSearchQuery] = useState('');

  // États pour le refresh global et la source
  const [refreshKey, setRefreshKey] = useState(0);
  const [source, setSource] = useState('cache');

  // Fermer le menu déroulant au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Thème & Fullscreen ---
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => console.error(err));
    } else {
      document.exitFullscreen();
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
  };

  // Forcer l'actualisation
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    setSource('live (forcé)');
    setTimeout(() => setSource('live'), 1000);
  };

  // Valeur du contexte
  const contextValue = {
    refreshKey,
    source,
    setSource,
  };

  // --- Rendu du contenu en fonction de l'onglet actif (avec Provider) ---
  const renderContent = () => {
    const ComponentWrapper = ({ children }) => (
      <div className="tab-transition">{children}</div>
    );

    let content = null;
    switch (activeTab) {
      case 'dashboard':
        content = <PresenceDashboard userId={userId} key={refreshKey} />;
        break;
      case 'gestion':
        content = <ManagePresences userId={userId} key={refreshKey} />;
        break;
      case 'info':
        content = <PresenceGrouped userId={userId} key={refreshKey} />;
        break;
      case 'historique':
        content = <HistoriquePresence userId={userId} key={refreshKey} />;
        break;
      default:
        content = null;
    }

    return (
      <PresencesContext.Provider value={contextValue}>
        <ComponentWrapper>{content}</ComponentWrapper>
      </PresencesContext.Provider>
    );
  };

  // Onglets pour la tab bar mobile
  const mobileTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: faTachometerAlt },
    { id: 'gestion', label: 'Gestion', icon: faCalendarCheck },
    { id: 'info', label: 'Dates', icon: faCalendarDays },
    { id: 'historique', label: 'Historique', icon: faHistory },
  ];

  return (
    <>
      <style>{`
        /* ============================================================
           DARK BLUE GLASSMORPHISM – CUSTOM PRESENCES (mobile optimisé)
           ============================================================ */
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.35);
          --bg-primary: #0b1428;
          --bg-secondary: #0a0f1f;
          --card-bg: rgba(20, 40, 80, 0.45);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-muted: rgba(255, 255, 255, 0.5);
          --header-height: 56px;
          --sidebar-width: 260px;
          --sidebar-collapsed-width: 80px;
          --safe-bottom: env(safe-area-inset-bottom, 0px);
        }

        * {
          -webkit-tap-highlight-color: transparent;
          box-sizing: border-box;
        }

        body {
          background: var(--bg-primary);
          background-image:
            radial-gradient(circle at 10% 20%, #132044 0%, transparent 50%),
            radial-gradient(circle at 90% 80%, #0f1a33 0%, transparent 50%);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          margin: 0;
          min-height: 100vh;
        }

        /* ===== HEADER MOBILE (compact) ===== */
        .app-header {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1030;
          background: rgba(10, 15, 31, 0.75);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          padding: 6px 12px;
          display: flex; align-items: center; justify-content: space-between;
          gap: 8px;
          border-bottom: 1px solid rgba(59, 130, 246, 0.08);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          height: var(--header-height);
        }
        .app-header .brand { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
        .app-header .brand img { height: 24px; filter: drop-shadow(0 0 10px rgba(59,130,246,0.3)); }
        .app-header .brand .brand-name {
          font-weight: 700; font-size: 0.9rem;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-dark-blue));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          display: none;
        }
        @media (min-width: 576px) { .app-header .brand .brand-name { display: inline; } }

        .app-header .header-search {
          flex: 1;
          background: rgba(255,255,255,0.04);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 12px;
          display: flex; align-items: center; gap: 6px;
          transition: all 0.3s ease;
          min-width: 0;
        }
        .app-header .header-search:focus-within {
          background: rgba(255,255,255,0.08);
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
        }
        .app-header .header-search input {
          background: transparent; border: none; outline: none;
          color: var(--text-primary); font-size: 0.8rem; width: 100%; padding: 4px 0;
        }
        .app-header .header-search input::placeholder { color: var(--text-muted); }
        .app-header .header-search .search-icon { color: var(--text-muted); font-size: 0.8rem; }

        .app-header .header-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
        .app-header .header-actions button {
          background: rgba(255,255,255,0.04);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-primary);
          width: 34px; height: 34px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s ease; font-size: 1rem;
        }
        .app-header .header-actions button:active {
          transform: scale(0.92);
          background: rgba(59, 130, 246, 0.12);
          border-color: rgba(59, 130, 246, 0.2);
        }

        /* ===== DESKTOP TOPBAR ===== */
        .desktop-topbar {
          display: none;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 28px;
          padding: 16px 24px;
          background: rgba(20, 40, 80, 0.3);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(59, 130, 246, 0.08);
          border-radius: 24px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
          width: 100%;
        }
        @media (min-width: 992px) { .desktop-topbar { display: flex; } }

        .topbar-left { display: flex; align-items: center; gap: 14px; }
        .topbar-toggle-btn {
          background: rgba(255,255,255,0.04);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-primary);
          width: 38px; height: 38px; border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s ease;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }
        .topbar-toggle-btn:hover {
          background: rgba(59, 130, 246, 0.12);
          border-color: rgba(59, 130, 246, 0.2);
          color: var(--accent-blue);
        }
        .topbar-toggle-btn:active { transform: scale(0.92); }

        .topbar-title {
          font-size: 1.4rem; font-weight: 700; margin: 0;
          background: linear-gradient(135deg, #e0e7ff, #a5b4fc);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          letter-spacing: -0.3px;
        }

        .topbar-right { display: flex; align-items: center; gap: 14px; position: relative; }

        .badge-source {
          font-size: 0.7rem; padding: 4px 14px; border-radius: 40px;
          background: rgba(59,130,246,0.12);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap; font-weight: 500; backdrop-filter: blur(4px);
        }
        .badge-source.live {
          background: rgba(46,204,113,0.12);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.12);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .topbar-btn {
          background: rgba(255,255,255,0.04);
          backdrop-filter: blur(4px);
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-primary);
          padding: 8px 18px; border-radius: 30px;
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 0.85rem; font-weight: 500;
          transition: all 0.25s ease; cursor: pointer;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }
        .topbar-btn:hover {
          background: rgba(59,130,246,0.12);
          border-color: rgba(59,130,246,0.2);
          color: var(--accent-blue);
        }
        .topbar-btn.primary {
          background: var(--accent-blue);
          color: white; border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .topbar-btn.primary:hover {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59,130,246,0.4);
          color: white;
        }

        .topbar-avatar-wrapper { position: relative; }
        .topbar-avatar {
          width: 40px; height: 40px; border-radius: 50%;
          background: linear-gradient(135deg, var(--accent-blue), var(--accent-dark-blue));
          border: 2px solid rgba(255,255,255,0.1);
          color: white; font-weight: 600; font-size: 1rem;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s ease;
          box-shadow: 0 4px 14px rgba(59,130,246,0.3);
        }
        .topbar-avatar:hover { transform: scale(1.05); box-shadow: 0 6px 24px rgba(59,130,246,0.5); }

        .profile-dropdown {
          position: absolute; top: calc(100% + 12px); right: 0; width: 200px;
          background: rgba(10,20,45,0.9);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(59,130,246,0.1);
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.6);
          padding: 6px; z-index: 1050;
          opacity: 0; visibility: hidden; transform: translateY(-8px) scale(0.96);
          transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
          transform-origin: top right;
        }
        .profile-dropdown.open { opacity: 1; visibility: visible; transform: translateY(0) scale(1); }
        .profile-dropdown .dropdown-item {
          display: flex; align-items: center; gap: 12px; padding: 10px 14px; border-radius: 10px;
          color: var(--text-primary); transition: background 0.15s ease; cursor: pointer; font-size: 0.9rem;
        }
        .profile-dropdown .dropdown-item:hover { background: rgba(255,255,255,0.04); }
        .profile-dropdown .dropdown-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 4px 10px; }
        .profile-dropdown .dropdown-logout { color: #f87171; }
        .profile-dropdown .dropdown-logout:hover { background: rgba(239,68,68,0.08); color: #ef4444; }

        /* ===== CONTENU PRINCIPAL (mobile optimisé) ===== */
        .main-content {
          padding: 12px 12px 20px;
          min-height: 80vh;
          padding-top: calc(var(--header-height) + 12px);
          transition: margin-left 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        @media (min-width: 992px) {
          body { padding-bottom: 0; }
          .app-header { display: none; }
          .main-content {
            margin-left: var(--sidebar-width);
            padding: 32px 40px 40px;
            padding-top: 32px;
          }
          .main-content.sidebar-collapsed {
            margin-left: var(--sidebar-collapsed-width);
          }
        }

        /* ===== SIDEBAR WRAPPER ===== */
        .sidebar-wrapper {
          position: fixed; top: 0; left: 0; height: 100vh; z-index: 1040;
          width: var(--sidebar-width);
          transition: width 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
          overflow: hidden;
        }
        .sidebar-wrapper.collapsed { width: var(--sidebar-collapsed-width); }

        /* ===== ONGLETS DESKTOP ===== */
        .desktop-tabs {
          display: none;
          gap: 6px;
          margin-bottom: 28px;
          padding: 4px;
          background: rgba(20, 40, 80, 0.3);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(59, 130, 246, 0.06);
          border-radius: 40px;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
        }
        @media (min-width: 992px) { .desktop-tabs { display: flex; } }

        .tab-btn-desktop {
          padding: 10px 24px;
          border-radius: 30px;
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.25s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }
        .tab-btn-desktop:hover {
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
        }
        .tab-btn-desktop.active {
          background: rgba(59,130,246,0.12);
          border: 1px solid rgba(59,130,246,0.15);
          box-shadow: 0 0 30px rgba(59,130,246,0.05), inset 0 1px 0 rgba(255,255,255,0.04);
          color: var(--accent-blue);
          font-weight: 600;
          transform: scale(1.02);
        }
        .tab-btn-desktop.active svg {
          filter: drop-shadow(0 2px 12px rgba(59,130,246,0.2));
        }

        /* ===== CONTENU DES ONGLETS (mobile optimisé) ===== */
        .tab-transition {
          background: var(--card-bg);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: 1rem;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255,255,255,0.02);
          animation: fadeSlideUp 0.35s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        @media (min-width: 992px) {
          .tab-transition { padding: 2rem; border-radius: 28px; }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* ===== TAB BAR MOBILE (NATIVE) ===== */
        .app-tab-bar {
          position: fixed; bottom: 0; left: 0; right: 0; height: 64px;
          padding-bottom: var(--safe-bottom);
          background: rgba(11,20,40,0.85);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border-top: 1px solid rgba(59,130,246,0.06);
          display: flex; align-items: stretch; justify-content: space-around;
          z-index: 1050;
          box-shadow: 0 -4px 20px rgba(0,0,0,0.3);
        }
        .app-tab-bar .tab-item {
          display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1;
          background: none; border: none; color: var(--text-muted);
          font-size: 0.5rem; font-weight: 500; letter-spacing: 0.2px;
          transition: color 0.2s; position: relative; padding: 2px 0;
          cursor: pointer; touch-action: manipulation; -webkit-tap-highlight-color: transparent;
        }
        .app-tab-bar .tab-item.active { color: var(--accent-blue); }
        .app-tab-bar .tab-item .tab-icon {
          font-size: 1.4rem; margin-bottom: 0px;
          transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .app-tab-bar .tab-item.active .tab-icon {
          transform: translateY(-2px) scale(1.1);
          filter: drop-shadow(0 2px 10px rgba(59,130,246,0.3));
        }
        .app-tab-bar .tab-item .tab-label { font-size: 0.55rem; font-weight: 500; opacity: 0.7; margin-top: 1px; }
        .app-tab-bar .tab-item.active .tab-label { opacity: 1; }
        .app-tab-bar .tab-item .tab-indicator {
          position: absolute; top: 2px; left: 50%; transform: translateX(-50%);
          width: 18px; height: 3px; background: var(--accent-blue);
          border-radius: 0 0 4px 4px; opacity: 0; transition: opacity 0.3s ease;
        }
        .app-tab-bar .tab-item.active .tab-indicator { opacity: 1; }
        .app-tab-bar .tab-item:active { transform: scale(0.92); transition: transform 0.05s; }

        /* ===== FAB REFRESH MOBILE ===== */
        .fab-refresh {
          position: fixed; bottom: 80px; right: 16px;
          width: 50px; height: 50px; border-radius: 50%;
          background: var(--accent-blue); color: white; border: none;
          box-shadow: 0 8px 24px rgba(59,130,246,0.4);
          font-size: 1.3rem; display: flex; align-items: center; justify-content: center;
          z-index: 1060; transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
          touch-action: manipulation; cursor: pointer;
        }
        .fab-refresh:active { transform: scale(0.9); box-shadow: 0 4px 12px rgba(59,130,246,0.3); }
        .fab-refresh .spinning { animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (min-width: 992px) { .fab-refresh { display: none; } }

        /* ===== OFFCANVAS SIDEBAR ===== */
        .offcanvas-glass {
          background: rgba(10,20,45,0.9) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border-right: 1px solid rgba(59,130,246,0.08) !important;
        }
        .offcanvas-glass .btn-close { filter: invert(1) brightness(200%); opacity: 0.5; }

        /* ===== SCROLLBAR ===== */
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(59,130,246,0.3); border-radius: 4px; }

        /* ===== UTILITAIRES ===== */
        .d-none { display: none; }
        @media (min-width: 992px) {
          .d-lg-none { display: none !important; }
          .d-lg-block { display: block !important; }
          .d-lg-flex { display: flex !important; }
        }
        .text-muted { color: var(--text-muted); }
        .text-secondary { color: #94a3b8; }
        .fw-bold { font-weight: 600; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .gap-4 { gap: 1.5rem; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .me-2 { margin-right: 0.5rem; }
        .my-1 { margin: 0.25rem 0; }
        .opacity-10 { opacity: 0.1; }
        .border-0 { border: none; }
        .bg-transparent { background: transparent; }
        .position-relative { position: relative; }
        .w-100 { width: 100%; }
      `}</style>

      {/* ===== SIDEBAR DESKTOP ===== */}
      <div className={`sidebar-wrapper d-none d-lg-block ${sidebarCollapsed ? 'collapsed' : ''}`}>
        {fonction === 'RH' && (
          <SidebarRH userId={userId} theme={theme} fonction={fonction} />
        )}
        {(fonction === 'SUPER ADMIN' || fonction === 'DSI') && (
          <Sidebar userId={userId} theme={theme} fonction={fonction} />
        )}
          {(fonction === 'COMPTABLE') && (
          <SidebarCOMPTABLE userId={userId} theme={theme} fonction={fonction}  />
        )}
      </div>

      {/* ===== SIDEBAR MOBILE (Offcanvas) ===== */}
      <Offcanvas show={showMobileMenu} onHide={() => setShowMobileMenu(false)} placement="start" className="offcanvas-glass">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="text-white fw-bold">
            <img src={Logo} alt="logo" height="28" className="me-2" /> Menu
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {fonction === 'RH' && (
            <SidebarRH
              userId={userId} theme={theme} fonction={fonction}
              onLinkClick={() => setShowMobileMenu(false)}
            />
          )}
          {(fonction === 'SUPER ADMIN' || fonction === 'DSI') && (
            <Sidebar
              userId={userId} theme={theme} fonction={fonction}
              onLinkClick={() => setShowMobileMenu(false)}
            />
          )}

            {(fonction === 'COMPTABLE') && (
            <SidebarCOMPTABLE
              userId={userId} theme={theme} fonction={fonction} 
              onLinkClick={() => setShowMobileMenu(false)}
            />
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* ===== HEADER MOBILE ===== */}
      <header className="app-header d-lg-none">
        <div className="brand">
          <img src={Logo} alt="SuperMarket" />
          <span className="brand-name">App Panel</span>
        </div>
      
        <div className="header-actions">
          <button onClick={toggleFullscreen} title="Plein écran">
            <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
          </button>
          <button onClick={() => setShowMobileMenu(true)}>
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      </header>

      {/* ===== CONTENU PRINCIPAL ===== */}
      <main className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* TOPBAR DESKTOP */}
        <div className="desktop-topbar">
          <div className="topbar-left">
            <button className="topbar-toggle-btn" onClick={toggleSidebar} title="Réduire/agrandir le menu">
              <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
            </button>
            <h2 className="topbar-title">✅ Gestion des présences</h2>
          </div>
          <div className="topbar-right">
            <span className={`badge-source ${source === 'live' ? 'live' : source === 'cache (périmé)' ? 'warning' : ''}`}>
              {source === 'cache' ? '📦 Cache' :
               source === 'cache (périmé)' ? '⚠️ Cache périmé' :
               source === 'live' ? '🔄 Live' :
               source === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="topbar-btn primary" onClick={handleRefresh}>
              <FontAwesomeIcon icon={faSync} /> Actualiser
            </button>
            <button className="topbar-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}>
              <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} />
            </button>
            <div className="topbar-avatar-wrapper" ref={dropdownRef}>
              <div className="topbar-avatar" onClick={() => setShowProfileMenu(!showProfileMenu)}>
                {userData.name.charAt(0)}
              </div>
              <div className={`profile-dropdown ${showProfileMenu ? 'open' : ''}`}>
                <div className="dropdown-item"><FontAwesomeIcon icon={faUser} /> Mon Profil</div>
                <div className="dropdown-divider" />
                <div className="dropdown-item dropdown-logout" onClick={handleLogout}>
                  <FontAwesomeIcon icon={faSignOutAlt} /> Déconnexion
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ONGLETS DESKTOP */}
        <div className="desktop-tabs">
          <button className={`tab-btn-desktop ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
            <FontAwesomeIcon icon={faTachometerAlt} /> Tableau de bord
          </button>
          <button className={`tab-btn-desktop ${activeTab === 'gestion' ? 'active' : ''}`} onClick={() => setActiveTab('gestion')}>
            <FontAwesomeIcon icon={faCalendarCheck} /> Gestion des présences
          </button>
           <button className={`tab-btn-desktop ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>
            <FontAwesomeIcon icon={faCalendarDays} /> Info par date
          </button>
          <button className={`tab-btn-desktop ${activeTab === 'historique' ? 'active' : ''}`} onClick={() => setActiveTab('historique')}>
            <FontAwesomeIcon icon={faHistory} /> Historique
          </button>
        </div>

        {/* CONTENU DES ONGLETS */}
        <div className="d-none d-lg-block">{renderContent()}</div>
        <div className="d-lg-none">{renderContent()}</div>
      </main>

      {/* ===== FAB REFRESH MOBILE ===== */}
      <button className="fab-refresh d-lg-none" onClick={handleRefresh} title="Actualiser">
        <FontAwesomeIcon icon={faSync} className={refreshKey > 0 ? 'spinning' : ''} />
      </button>

      {/* ===== TAB BAR MOBILE ===== */}
      <div className="app-tab-bar d-lg-none">
        {mobileTabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <div className="tab-indicator" />
            <FontAwesomeIcon icon={tab.icon} className="tab-icon" />
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
        <button className="tab-item" onClick={handleLogout}>
          <div className="tab-indicator" style={{ background: '#f87171' }} />
          <FontAwesomeIcon icon={faSignOutAlt} className="tab-icon" style={{ color: '#f87171' }} />
          <span className="tab-label" style={{ color: '#f87171' }}>Déconnexion</span>
        </button>
      </div>
    </>
  );
};

export default CustomPresences;